import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../lib/api-client';
import { apiError } from '../../lib/utils';
import { Alert } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { ExcelButton, PageHead } from '../../components/ui/chrome';
import { ColumnFilterInputs, ColumnFilterToggle, useColumnSearch } from '../../components/ui/ColumnSearch';
import { Input, Select } from '../../components/ui/Input';
import { ScheduleOutlook, type ResourceReport } from './ScheduleOutlook';
import { monthContaining, periodKind, quarterContaining, shiftPeriod, todayIso, type PeriodRange } from './period';

function hours(value: number): string {
  return value.toLocaleString('en-GB', { maximumFractionDigits: 2 });
}

function percent(value: number | null): string {
  if (value == null) return 'n/a';
  return `${(value * 100).toLocaleString('en-GB', { maximumFractionDigits: 1 })}%`;
}

function rangeText(from: string, to: string): string {
  const format = (iso: string) => {
    const [year, month, day] = iso.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
  };
  return `${format(from)} - ${format(to)}`;
}

export function ResourcingReport({ embedded = false }: { embedded?: boolean }) {
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState<'schedule' | 'booked'>('schedule');
  const personSearch = useColumnSearch();
  const projectSearch = useColumnSearch();
  const today = todayIso();
  const current = quarterContaining(today);
  const fromKey = embedded ? 'rFrom' : 'from';
  const toKey = embedded ? 'rTo' : 'to';
  const from = params.get(fromKey) || current.from;
  const to = params.get(toKey) || current.to;
  const client = params.get('client') || '';
  const projectId = params.get('project') || '';
  const userId = params.get('person') || '';
  const billing = params.get('billing') || 'all';
  const status = params.get('status') || 'all';
  const kind = periodKind(from, to);
  const period = kind === 'quarter' ? quarterContaining(from) : kind === 'month' ? monthContaining(from) : { from, to, label: 'Custom' };
  const query = useQuery({
    queryKey: ['resourcing', from, to, client, projectId, userId, billing, status],
    queryFn: () => {
      const search = new URLSearchParams({ from, to, client, projectId, userId, billing, status });
      return apiGet<ResourceReport>(`/api/reports/resourcing?${search.toString()}`);
    },
  });

  function apply(next: Record<string, string>) {
    const merged = new URLSearchParams(params);
    merged.set(fromKey, from);
    merged.set(toKey, to);
    for (const [key, value] of Object.entries(next)) {
      const name = key === 'from' ? fromKey : key === 'to' ? toKey : key;
      if (value) merged.set(name, value);
      else merged.delete(name);
    }
    setParams(merged, { replace: true });
  }

  function applyPeriod(next: PeriodRange) {
    apply({ from: next.from, to: next.to });
  }

  const data = query.data;
  const projects = useMemo(() => {
    const rows = data?.choices.projects || [];
    return client ? rows.filter((row) => row.client === client) : rows;
  }, [data, client]);
  const booked = data?.booked.summary;
  const personRows = (data?.booked.byResource || []).filter((row) => personSearch.matches({
    person: row.resource,
    booked: hours(row.bookedHours),
    scheduled: hours(row.scheduledToDateHours),
    variance: hours(row.varianceHours),
    delivered: percent(row.deliveredVsPlan),
  }));
  const projectRows = (data?.booked.byProject || []).filter((row) => projectSearch.matches({
    client: row.client,
    project: row.project,
    booked: hours(row.bookedHours),
    scheduled: hours(row.scheduledToDateHours),
    variance: hours(row.varianceHours),
  }));
  const filtered = Boolean(client || projectId || userId || billing !== 'all' || status !== 'all');
  const periodLabel = kind === 'quarter' ? 'this quarter' : kind === 'month' ? 'this month' : 'this period';
  const columnLabel = kind === 'quarter' ? 'Quarter' : kind === 'month' ? 'Month' : 'Period';

  return (
    <div className="space-y-4">
      <PageHead
        crumbs={embedded ? [{ label: 'Dashboard', to: '/' }, { label: 'Resourcing' }] : [{ label: 'Reports', to: '/?report=receivable' }, { label: 'Resourcing' }]}
        title={tab === 'schedule' ? 'Schedule outlook' : 'Booked vs scheduled'}
        subtitle={tab === 'schedule' ? 'Clockify scheduled assignments by project and resource, by month or quarter.' : 'Clockify time entries against the schedule up to today. Leave projects are absence.'}
        actionsInline
        actions={(
          <>
            <div className="flex rounded-lg border border-[var(--bo-border)] p-0.5">
              <button type="button" className={`rounded-md px-3 py-1 text-sm ${kind === 'month' ? 'bg-[var(--bo-hover)]' : 'text-[var(--bo-muted)]'}`} onClick={() => applyPeriod(monthContaining(from))}>Month</button>
              <button type="button" className={`rounded-md px-3 py-1 text-sm ${kind === 'quarter' ? 'bg-[var(--bo-hover)]' : 'text-[var(--bo-muted)]'}`} onClick={() => applyPeriod(quarterContaining(from))}>Quarter</button>
            </div>
            <button type="button" aria-label="Previous period" className="rounded-md border border-[var(--bo-border)] px-2 py-1" onClick={() => applyPeriod(shiftPeriod(from, to, -1))}>‹</button>
            <div className="min-w-28 text-right">
              <div className="font-medium">{period.label}</div>
              <div className="text-xs text-[var(--bo-faint)]">{rangeText(from, to)}</div>
            </div>
            <button type="button" aria-label="Next period" className="rounded-md border border-[var(--bo-border)] px-2 py-1" onClick={() => applyPeriod(shiftPeriod(from, to, 1))}>›</button>
          </>
        )}
      />
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant={tab === 'schedule' ? 'default' : 'outline'} onClick={() => setTab('schedule')}>Schedule outlook</Button>
        <Button size="sm" variant={tab === 'booked' ? 'default' : 'outline'} onClick={() => setTab('booked')}>Booked vs scheduled</Button>
        <label className="text-sm text-[var(--bo-muted)]">From
          <Input className="mt-1" type="date" aria-label="From" value={from} onChange={(event) => apply({ from: event.target.value })} />
        </label>
        <label className="text-sm text-[var(--bo-muted)]">To
          <Input className="mt-1" type="date" aria-label="To" value={to} onChange={(event) => apply({ to: event.target.value })} />
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[var(--bo-border)] bg-[var(--bo-surface)] p-3">
        <Select aria-label="Client" value={client} onChange={(event) => apply({ client: event.target.value, project: '' })}>
          <option value="">All clients</option>
          {(data?.choices.clients || []).map((name) => <option key={name} value={name}>{name}</option>)}
        </Select>
        <Select aria-label="Project" value={projectId} onChange={(event) => apply({ project: event.target.value })}>
          <option value="">All projects</option>
          {projects.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
        </Select>
        <Select aria-label="Person" value={userId} onChange={(event) => apply({ person: event.target.value })}>
          <option value="">All people</option>
          {(data?.choices.people || []).map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
        </Select>
        <span className="text-xs text-[var(--bo-faint)]">Status</span>
        <Pill active={status === 'all'} onClick={() => apply({ status: '' })}>All</Pill>
        <Pill active={status === 'published'} onClick={() => apply({ status: 'published' })}>Published</Pill>
        <Pill active={status === 'draft'} onClick={() => apply({ status: 'draft' })}>Draft</Pill>
        <span className="text-xs text-[var(--bo-faint)]">Billing</span>
        <Pill active={billing === 'all'} onClick={() => apply({ billing: '' })}>All</Pill>
        <Pill active={billing === 'billable'} onClick={() => apply({ billing: 'billable' })}>Billable</Pill>
        <Pill active={billing === 'non_billable'} onClick={() => apply({ billing: 'non_billable' })}>Non-billable</Pill>
        {filtered ? <button type="button" className="text-sm text-[var(--bo-link)]" onClick={() => apply({ client: '', project: '', person: '', billing: '', status: '' })}>Clear filters</button> : null}
      </div>
      {query.isError ? <Alert error>{apiError(query.error, 'Could not load resourcing')}</Alert> : null}
      {query.isLoading ? <div className="text-sm text-[var(--bo-muted)]">Loading from Clockify</div> : null}
      {data && tab === 'schedule' ? (
        <ScheduleOutlook
          report={data}
          today={today}
          periodLabel={periodLabel}
          columnLabel={columnLabel}
          projectId={projectId}
          userId={userId}
          updatedAt={query.dataUpdatedAt}
          refreshing={query.isFetching}
          onFilter={apply}
          onRefresh={() => { void query.refetch(); }}
        />
      ) : null}
      {tab === 'booked' && booked ? (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {[
            ['Booked hours', hours(booked.bookedHours)],
            ['Scheduled to date', hours(booked.scheduledToDateHours)],
            ['Variance', hours(booked.varianceHours)],
            ['Variance %', percent(booked.variancePct)],
            ['Delivered vs plan', percent(booked.deliveredVsPlan)],
            ['Unscheduled', hours(booked.unscheduledHours)],
            ['Absence', hours(booked.absenceHours)],
            ['Compared to', data?.comparedTo || 'n/a'],
          ].map(([label, value]) => (
            <div key={label} className="rounded-lg border border-[var(--bo-border)] bg-[var(--bo-surface)] px-3 py-2">
              <div className="text-xs text-[var(--bo-faint)]">{label}</div>
              <div className="text-lg font-semibold">{value}</div>
            </div>
          ))}
        </div>
      ) : null}
      {data && tab === 'booked' ? (
        <div className="grid gap-4 xl:grid-cols-2">
          <div className="overflow-x-auto rounded-lg border border-[var(--bo-border)] bg-[var(--bo-surface)]">
            <div className="flex justify-end px-3 pt-2"><ExcelButton filename="Booked by person" headers={['Person', 'Booked', 'Scheduled to date', 'Variance', 'Delivered']} rows={personRows.map((row) => [row.resource, row.bookedHours, row.scheduledToDateHours, row.varianceHours, row.deliveredVsPlan == null ? '' : row.deliveredVsPlan])} /></div>
            <table className="bo-cards min-w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--bo-border)] text-left text-[var(--bo-faint)]">
                  <th className="px-3 py-2 font-medium">Person</th>
                  <th className="px-3 py-2 font-medium">Booked</th>
                  <th className="px-3 py-2 font-medium">Scheduled to date</th>
                  <th className="px-3 py-2 font-medium">Variance</th>
                  <th className="px-3 py-2 font-medium">Delivered</th>
                  <th className="w-8 px-2 py-2 text-right"><ColumnFilterToggle open={personSearch.open} onClick={personSearch.toggle} /></th>
                </tr>
                {personSearch.open ? <ColumnFilterInputs columns={[{ key: 'person', label: 'Person' }, { key: 'booked', label: 'Booked' }, { key: 'scheduled', label: 'Scheduled to date' }, { key: 'variance', label: 'Variance' }, { key: 'delivered', label: 'Delivered' }]} draft={personSearch.draft} onChange={personSearch.set} trailing /> : null}
              </thead>
              <tbody>
                {personRows.map((row) => (
                  <tr key={row.userId} className={`cursor-pointer border-b border-[var(--bo-border)] hover:bg-[var(--bo-hover)] ${userId === row.userId ? 'bg-[var(--bo-hover)]' : ''}`} onClick={() => apply({ person: userId === row.userId ? '' : row.userId })}>
                    <td className="px-3 py-2" data-label="Person">{row.resource}</td>
                    <td className="px-3 py-2" data-label="Booked">{hours(row.bookedHours)}</td>
                    <td className="px-3 py-2" data-label="Scheduled to date">{hours(row.scheduledToDateHours)}</td>
                    <td className="px-3 py-2" data-label="Variance">{hours(row.varianceHours)}</td>
                    <td className="px-3 py-2" data-label="Delivered">{percent(row.deliveredVsPlan)}</td>
                    <td />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="overflow-x-auto rounded-lg border border-[var(--bo-border)] bg-[var(--bo-surface)]">
            <div className="flex justify-end px-3 pt-2"><ExcelButton filename="Booked by project" headers={['Client', 'Project', 'Booked', 'Scheduled to date', 'Variance']} rows={projectRows.map((row) => [row.client, row.project, row.bookedHours, row.scheduledToDateHours, row.varianceHours])} /></div>
            <table className="bo-cards min-w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--bo-border)] text-left text-[var(--bo-faint)]">
                  <th className="px-3 py-2 font-medium">Client</th>
                  <th className="px-3 py-2 font-medium">Project</th>
                  <th className="px-3 py-2 font-medium">Booked</th>
                  <th className="px-3 py-2 font-medium">Scheduled to date</th>
                  <th className="px-3 py-2 font-medium">Variance</th>
                  <th className="w-8 px-2 py-2 text-right"><ColumnFilterToggle open={projectSearch.open} onClick={projectSearch.toggle} /></th>
                </tr>
                {projectSearch.open ? <ColumnFilterInputs columns={[{ key: 'client', label: 'Client' }, { key: 'project', label: 'Project' }, { key: 'booked', label: 'Booked' }, { key: 'scheduled', label: 'Scheduled to date' }, { key: 'variance', label: 'Variance' }]} draft={projectSearch.draft} onChange={projectSearch.set} trailing /> : null}
              </thead>
              <tbody>
                {projectRows.map((row) => (
                  <tr key={row.projectId} className={`cursor-pointer border-b border-[var(--bo-border)] hover:bg-[var(--bo-hover)] ${projectId === row.projectId ? 'bg-[var(--bo-hover)]' : ''}`} onClick={() => apply({ client: row.client, project: projectId === row.projectId ? '' : row.projectId })}>
                    <td className="px-3 py-2" data-label="Client">{row.client}</td>
                    <td className="px-3 py-2" data-label="Project">{row.project}</td>
                    <td className="px-3 py-2" data-label="Booked">{hours(row.bookedHours)}</td>
                    <td className="px-3 py-2" data-label="Scheduled to date">{hours(row.scheduledToDateHours)}</td>
                    <td className="px-3 py-2" data-label="Variance">{hours(row.varianceHours)}</td>
                    <td />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Pill({ active, onClick, children }: { active: boolean; onClick: () => void; children: string }) {
  return (
    <button type="button" className={`rounded-full border px-3 py-1 text-sm ${active ? 'border-[#895CF6] text-[var(--bo-text)]' : 'border-[var(--bo-border)] text-[var(--bo-muted)]'}`} onClick={onClick}>
      {children}
    </button>
  );
}
